import React, { useState } from 'react'
import styled from 'styled-components'
import { Dimmer, Loader, Icon } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { File as DashboardFile } from '~/api/services/dashboard'
import { CloudStorageService } from '~/api/services/cloudStorage'
import { DownloadService } from '~/api/services/downloadFiles'
import notify from '~/components/Notify'
import { formatFileSize } from '~/utils/helpers'
import PreviewFileModal from '~/pages/Farmer/CloudStorage/FilesLayout/Modals/PreviewFileModal'
import { FileSrcType } from '~/pages/Farmer/CloudStorage/FilesLayout'

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  padding: 20px 0;
`

const VideoCard = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  height: 200px;
`

const FileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  cursor: pointer;
  gap: 10px;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`

function extractYoutubeId(link: string | null): string | null {
  if (!link) return null
  const match = link.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  return match ? match[1] : null
}

interface Props {
  files: DashboardFile[]
}

const CooperativeDashboard: React.FC<Props> = ({ files }) => {
  const { t } = useTranslation()
  const [fileSource, setFileSource] = useState<FileSrcType | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  async function fetchPreviewData(file: DashboardFile) {
    setIsBusy(true)
    setFileSource({ src: true })
    try {
      const response = await CloudStorageService.getTempLinkForFile(file.id, { fileName: file.filename ?? file.title })
      setFileSource({
        src: response.data,
        type: file.filetype,
        createdAt: file.createdAt,
        size: file.filesize ?? undefined,
        name: file.filename ?? file.title,
        id: file.id
      })
      await CloudStorageService.increaseViewCounter(file.id)
    } catch (error) {
      setFileSource(null)
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  async function downloadFile(file: DashboardFile) {
    setIsBusy(true)
    try {
      await DownloadService.downloadFile(`cloud-storage/files/${file.id}/get-file`, file.title, null, true)
      await CloudStorageService.increaseViewCounter(file.id)
    } catch (error) {
      if (error?.response?.data) {
        notify(error?.response?.data?.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  function handleFileClick(file: DashboardFile) {
    if (file.filetype?.toLowerCase().includes('pdf')) {
      fetchPreviewData(file)
    } else {
      downloadFile(file)
    }
  }

  function renderFileCard(file: DashboardFile) {
    const youtubeId = extractYoutubeId(file.link)

    if (youtubeId) {
      return (
        <VideoCard key={file.id}>
          <iframe
            width='100%'
            height='200'
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={file.title}
            frameBorder='0'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
            style={{ display: 'block' }}
          />
        </VideoCard>
      )
    }

    const isPdf = file.filetype?.toLowerCase().includes('pdf')

    return (
      <VideoCard key={file.id}>
        <FileCardContent onClick={() => handleFileClick(file)}>
          <Icon
            name={isPdf ? 'file pdf' : 'file'}
            size='massive'
            style={{ color: isPdf ? '#e53935' : '#9e9e9e' }}
          />
          <div style={{ textAlign: 'center', fontSize: '13px', color: '#333', fontWeight: 500 }}>{file.title}</div>
          {file.filename && (
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#999' }}>{file.filename}</div>
          )}
          {file.filesize != null && (
            <div style={{ fontSize: '11px', color: '#bbb' }}>{formatFileSize(file.filesize)}</div>
          )}
        </FileCardContent>
      </VideoCard>
    )
  }

  function renderFolderFiles(files: DashboardFile[]) {
    if (!files?.length) {
      return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>{t('No files in this folder.')}</div>
    }
    return (
      <VideoGrid>
        {files.map(file => renderFileCard(file))}
      </VideoGrid>
    )
  }

  return (
    <div style={{ position: 'relative', marginTop: '1em' }}>
      <Dimmer inverted active={isBusy}>
        <Loader active={isBusy} />
      </Dimmer>
      <PreviewFileModal
        fileSource={fileSource}
        dataFiltered={{ folderId: null }}
        handleClose={() => setFileSource(null)}
        files={null}
        setFiles={() => console.log('set')}
        isBusy={isBusy}
      />
      {renderFolderFiles(files)}
    </div>
  )
}

export default CooperativeDashboard
